// eslint-disable-next-line no-restricted-imports
import { Button as AntButton } from 'antd';
import { ButtonProps as AntButtonProps } from 'antd/es/button';
import { ButtonType as AntButtonType } from 'antd/es/button/button';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import React from 'react';
import styled, { css } from 'styled-components';
import { ifNotProp, ifProp, prop, switchProp } from 'styled-tools';
import { withHideIfNotAllowed } from 'components/organisms/permission-controller';

type IconPlacementType = 'left' | 'right';

declare const CustomButtonTypes: ['tertiary', 'success'];
type CustomButtonType = typeof CustomButtonTypes[number];

type OzoneButtonType = AntButtonType | CustomButtonType;

export interface ButtonProps extends Omit<AntButtonProps, 'type'> {
  iconPlacement?: IconPlacementType;
  type?: OzoneButtonType;
  hideHoverEffect?: boolean;
  hideHoverBorderRadius?: boolean;
}

const defaultIconPlacement = 'left';
const DefaultHorizontalPadding = css`
  padding-left: var(--spacing-05);
  padding-right: var(--spacing-05);
  ${ifProp(
    'icon',
    switchProp(prop<IconPlacementType>('iconPlacement', defaultIconPlacement), {
      right: css`
        & > svg {
          margin-right: 0;
          margin-left: var(--spacing-02);
        }
      `,
    }),
  )};
`;

const IconOnlyButton = css`
  ${ifProp(
    'icon',
    ifNotProp(
      'children',
      css`
        justify-content: center;
        &[disabled] > svg {
          fill-opacity: 0.4;
        }
        & > svg {
          margin: 0;
        }
        padding: 0;
        ${switchProp(prop<SizeType>('size', 'middle'), {
          middle: css`
            width: var(--input-height-default);
            min-width: var(--input-height-default);
            height: var(--input-height-default);
          `,
          small: css`
            width: var(--input-size-sm);
            min-width: var(--input-size-sm);
            height: var(--input-size-sm);
          `,
          large: css`
            width: var(--input-height-lg);
            min-width: var(--input-height-lg);
            height: var(--input-height-lg);
          `,
        })};
      `,
    ),
  )};
`;

export const PrimaryButtonBackgroundStyle = css`
  text-shadow: none;
  &&:not([disabled]) {
    background-color: var(--brand-color);
    border-color: var(--brand-color);
    box-shadow: var(--shadow-highlight)
      rgba(var(--brand-color-in-rgb), var(--shadow-highlight-color-opacity));
  }
  &&:not([disabled]):hover,
  &&:not([disabled]):active {
    background-color: var(--brand-hover-color);
    border-color: var(--brand-hover-color);
  }
`;

export const TertiaryButtonStyle = css`
  &&:not([disabled]) {
    color: var(--brand-color);
    border-color: var(--brand-color);
  }
  &&:not([disabled]):hover,
  &&:not([disabled]):active {
    background-color: var(--brand-color);
    color: var(--white-100);
  }
`;

export const SuccessButtonStyle = css`
  &&:not([disabled]) {
    background-color: var(--green-50);
    border-color: var(--green-50);
    color: var(--white-100);
  }
  &&:not([disabled]):hover,
  &&:not([disabled]):active {
    background-color: var(--green-70);
    border-color: var(--green-70);
    color: var(--white-100);
  }
`;

const ButtonWithRef = React.forwardRef<any, ButtonProps>((props, ref) => (
  <AntButton ref={ref} {...props} type={props.type as AntButtonType} />
));
const ButtonWithPermissionCheck = withHideIfNotAllowed(ButtonWithRef);

export const Button = styled(ButtonWithPermissionCheck)<ButtonProps>`
  display: inline-flex;
  align-items: center;

  .ant-btn-loading-icon {
    margin-right: 4px;
  }

  ${switchProp(prop('type'), {
    primary: ifNotProp(
      'danger',
      css`
        ${PrimaryButtonBackgroundStyle};
      `,
    ),
  })};

  ${switchProp(prop('type'), {
    tertiary: ifNotProp(
      'danger',
      css`
        ${TertiaryButtonStyle};
      `,
    ),
  })};

  ${switchProp(prop('type'), {
    success: ifNotProp(
      'danger',
      css`
        ${SuccessButtonStyle};
      `,
    ),
  })};

  ${switchProp(prop<IconPlacementType>('iconPlacement', defaultIconPlacement), {
    left: css`
      flex-direction: row;
    `,
    right: css`
      ${ifProp(
        'icon',
        css`
          flex-direction: row-reverse; /* We know this implementation is bad. Instead of complaining, either fix it or back off. :P */
        `,
      )};
    `,
  })};

  ${switchProp(prop<SizeType>('size', 'middle'), {
    middle: css`
      height: var(--input-height-default);
      padding: calc(0.675rem - 3px) var(--spacing-09) calc(0.675rem - 3px)
        var(--spacing-05);
      ${ifProp(
        'icon',
        css`
          padding-left: var(--spacing-04);
          ${switchProp(
            prop<IconPlacementType>('iconPlacement', defaultIconPlacement),
            {
              left: css`
                padding-right: var(--spacing-07);
                & > svg {
                  margin-right: var(--spacing-04);
                }
              `,
              right: css`
                padding-right: var(--spacing-04);
                & .ant-btn-loading-icon,
                & > svg {
                  margin-right: 0;
                  margin-left: var(--layout-03);
                }
              `,
            },
          )};
        `,
      )};
    `,
    small: css`
      height: var(--input-size-sm);
      padding: calc(0.375rem - 3px) var(--spacing-09) calc(0.375rem - 3px)
        var(--spacing-05);
      ${ifProp(
        'icon',
        css`
          padding-left: var(--spacing-03);
          ${switchProp(
            prop<IconPlacementType>('iconPlacement', defaultIconPlacement),
            {
              left: css`
                padding-right: var(--spacing-07);
                & > svg {
                  margin-right: var(--spacing-03);
                }
              `,
              right: css`
                padding-right: var(--spacing-03);
                & .ant-btn-loading-icon,
                & > svg {
                  margin-right: 0;
                  margin-left: var(--layout-03);
                }
              `,
            },
          )};
        `,
      )};
    `,
    large: css`
      height: var(--input-height-lg);
      padding-top: calc(0.875rem - 3px);
      padding-bottom: calc(0.875rem - 3px);
      padding-right: var(--spacing-09);
      ${ifProp(
        'icon',
        css`
          padding-left: var(--spacing-05);
          ${switchProp(
            prop<IconPlacementType>('iconPlacement', defaultIconPlacement),
            {
              left: css`
                padding-right: var(--spacing-07);
                & > svg {
                  margin-right: var(--layout-01);
                }
              `,
              right: css`
                padding-right: var(--spacing-05);
                & .ant-btn-loading-icon,
                & > svg {
                  margin-right: 0;
                  margin-left: var(--layout-03);
                }
              `,
            },
          )};
        `,
      )};
    `,
  })};

  ${switchProp(prop<OzoneButtonType>('type', 'default'), {
    primary: ifNotProp(
      'danger',
      css`
        &:not([disabled]):hover {
          background-color: var(--blue-70);
        }
        &:active {
          background-color: var(--blue-80);
        }
      `,
    ),
    text: css`
      ${DefaultHorizontalPadding}
      &:hover, &:active, &:focus {
        /* to match slight grey color hover which appears on other buttons */
        ${ifProp(
          'hideHoverEffect',
          css`
            background-color: transparent;
          `,
          css`
            background-color: var(--black-with-opacity);
          `,
        )};
        ${ifProp(
          'hideHoverBorderRadius',
          css`
            border-radius: 0;
          `,
          css`
            border-radius: var(--border-radius-default);
          `,
        )};
      }
      &:disabled:disabled {
        background-color: transparent;
      }
    `,
    link: css`
      ${DefaultHorizontalPadding}
      &:hover,
      &:active,
      &:focus {
        color: var(--blue-70);
        /* to match slight grey color hover which appears on other buttons */
        ${ifProp(
          'hideHoverEffect',
          css`
            text-decoration: none;
          `,
          css`
            text-decoration: underline;
          `,
        )};
        ${ifProp(
          'hideHoverBorderRadius',
          css`
            border-radius: 0;
          `,
          css`
            border-radius: var(--border-radius-default);
          `,
        )};
      }
      &:disabled:disabled {
        background-color: transparent;
      }
    `,
  })};

  ${IconOnlyButton};
`;
